import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/crm/CrmShell";
import { Btn, Field, inputCls, StatCard } from "@/components/crm/kit";
import { Database, Download, AlertTriangle, X } from "lucide-react";

const CMS_DEFAULT = "https://data.cms.gov/provider-summary-by-type-of-service/medicare-part-d-prescribers/medicare-part-d-prescribers-by-provider-and-drug";

const TONE = {
  queued: "bg-slate-100 text-slate-600", running: "bg-blue-100 text-blue-700",
  done: "bg-emerald-100 text-emerald-700", error: "bg-red-100 text-red-700",
  cancelled: "bg-amber-100 text-amber-700",
};

export default function DrugImport() {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [url, setUrl] = useState(CMS_DEFAULT);
  const [year, setYear] = useState("");
  const [maxRows, setMaxRows] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/medicare/drug-stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/medicare/drug-import").then((r) => setJobs(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const anyRunning = jobs.some((j) => ["running", "queued"].includes(j.status));
    if (!anyRunning) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [jobs, load]);

  const start = async () => {
    if (!url.trim()) { toast.error("Paste a CMS data URL"); return; }
    setBusy(true);
    try {
      await api.post("/medicare/drug-import", {
        url: url.trim(),
        dataset_year: year ? Number(year) : undefined,
        max_rows: maxRows ? Number(maxRows) : undefined,
      });
      toast.success("Import started — running in the background");
      setTimeout(load, 800);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cancel = async (id) => {
    try { await api.post(`/medicare/drug-import/${id}/cancel`); toast.success("Cancelling…"); setTimeout(load, 800); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <PageHeader title="Drug Data Import" subtitle="Load the CMS Medicare Part D 'by Provider and Drug' dataset — re-running updates existing rows." icon={Database} />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatCard label="Per-drug rows loaded" value={(stats?.total_rows ?? 0).toLocaleString()} />
        <StatCard label="Years" value={(stats?.years || []).join(", ") || "—"} />
        <StatCard label="Last import" value={stats?.last_import ? (stats.last_import.finished_at || "").slice(0, 10) : "—"} />
      </div>

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="mb-1 font-display font-bold text-slate-900">Start an import</h2>
        <p className="mb-4 text-xs text-slate-500">Paste the CMS dataset page, a direct CSV download link, or a data-api link. We resolve the file, stream it, and upsert by provider + drug + year.</p>
        <div className="space-y-3">
          <Field label="CMS data URL"><input data-testid="di-url" value={url} onChange={(e) => setUrl(e.target.value)} className={inputCls} placeholder="https://data.cms.gov/..." /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Dataset year (optional)"><input data-testid="di-year" value={year} onChange={(e) => setYear(e.target.value)} className={inputCls} placeholder="e.g. 2024 (auto-detected)" /></Field>
            <Field label="Max rows (optional — blank = full)"><input data-testid="di-maxrows" value={maxRows} onChange={(e) => setMaxRows(e.target.value)} className={inputCls} placeholder="e.g. 50000 to test" /></Field>
          </div>
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>The full dataset is ~28 million rows per year and can take a long time to import. It runs in the background — you can leave this page. Re-importing the same year updates existing rows (no duplicates).</span>
          </div>
          <Btn data-testid="di-start" onClick={start} disabled={busy}><Download className="h-4 w-4" /> {busy ? "Starting…" : "Start import"}</Btn>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="di-jobs">
        <h2 className="mb-3 font-display font-bold text-slate-900">Import history</h2>
        {jobs.length === 0 ? (
          <p className="text-sm text-slate-400">No imports yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="py-2 pr-3">Started</th><th className="py-2 pr-3">Year</th><th className="py-2 pr-3">Mode</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Processed</th><th className="py-2 pr-3">New</th><th className="py-2 pr-3">Updated</th><th className="py-2 pr-3"></th></tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-t border-slate-100" data-testid={`di-job-${j.id}`}>
                    <td className="py-2 pr-3 text-slate-500">{(j.started_at || j.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-2 pr-3 font-semibold text-slate-700">{j.dataset_year}</td>
                    <td className="py-2 pr-3 uppercase text-slate-500">{j.mode}</td>
                    <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${TONE[j.status] || "bg-slate-100 text-slate-500"}`}>{j.status}</span>{j.message && <span className="ml-1 text-xs text-red-500">{j.message}</span>}</td>
                    <td className="py-2 pr-3 font-bold text-slate-800">{(j.processed || 0).toLocaleString()}</td>
                    <td className="py-2 pr-3 text-emerald-600">{(j.inserted || 0).toLocaleString()}</td>
                    <td className="py-2 pr-3 text-blue-600">{(j.updated || 0).toLocaleString()}</td>
                    <td className="py-2 pr-3">{["running", "queued"].includes(j.status) && <button data-testid={`di-cancel-${j.id}`} onClick={() => cancel(j.id)} className="flex items-center gap-1 text-xs font-bold text-red-500 hover:underline"><X className="h-3 w-3" /> Cancel</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
